import React, { useState } from 'react';
import cx from 'classnames';

import styles from './ChartGroup.module.scss';
import Text from '../Text';

import { Chart, Series } from 'highcharts';
import Button from '../Button';
import Popover from '../Popover';
import Checkbox from '../Checkbox';
import { ChartProps } from 'Chart';

export interface Props {
  title: string;
  description?: string;
  children?: React.ReactElement<ChartProps>;
  className?: Parameters<typeof cx>[0];
}

const MockFilterGroup = ({ chart, ...restProps }: { chart: Chart | null }) => {
  const [seriesVisibility, setSeriesVisibility] = useState({
    'march-usage': true,
    'april-usage': true,
  });

  const [isOpen, setIsOpen] = useState(false);

  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);

  const toggleSeries = (ids: string[]) => {
    return ids.forEach(id => {
      if (!chart || !chart.get(id)) {
        return;
      }

      const series = chart.get(id) as Series;
      series.setVisible(seriesVisibility[id]);
    });
  };
  const handleSetOpen = () => setIsOpen(!isOpen);
  const applyFilters = () => {
    toggleSeries(Object.keys(seriesVisibility));
    setIsOpen(!isOpen);
  };

  if (!chart) {
    return <></>;
  }

  return (
    <div className={styles.filterGroup}>
      <Button
        ref={setTargetElement}
        onClick={handleSetOpen}
        inline
        small
        centered
      >
        Billing Period
      </Button>
      <Popover
        {...restProps}
        className={styles.filterPopover}
        isOpen={isOpen}
        label='Select billing Periods to view'
        setOpen={handleSetOpen}
        targetElement={targetElement}
      >
        <Popover.Header className={styles.filterPopoverHeader}>
          Billing Period
        </Popover.Header>
        <Popover.Body>
          <p>Select up to three months.</p>
          <Checkbox
            id='april-usage'
            value={seriesVisibility['april-usage']}
            onChange={() =>
              setSeriesVisibility({
                ...seriesVisibility,
                'april-usage': !seriesVisibility['april-usage'],
              })
            }
          >
            April 2022
          </Checkbox>
          <Checkbox
            id='march-usage'
            value={seriesVisibility['march-usage']}
            onChange={() =>
              setSeriesVisibility({
                ...seriesVisibility,
                'march-usage': !seriesVisibility['march-usage'],
              })
            }
          >
            March 2022
          </Checkbox>
        </Popover.Body>
        <Popover.Footer className={styles.filterPopoverFooter}>
          <Button
            className={styles.filterPopoverFooterButton}
            onClick={handleSetOpen}
            small
            centered
            tertiary
            inline
          >
            Cancel
          </Button>
          <Button
            className={styles.filterPopoverFooterButton}
            onClick={handleSetOpen}
            small
            centered
            secondary
            inline
          >
            Reset
          </Button>
          <Button
            className={styles.filterPopoverFooterButton}
            onClick={applyFilters}
            small
            centered
            inline
          >
            Apply
          </Button>
        </Popover.Footer>
      </Popover>
    </div>
  );
};

export const ChartGroup: React.FC<Props> = ({
  title,
  description,
  children,
  className,
}) => {
  const [chart, setChart] = useState(null);

  return (
    <div className={cx(styles.chart, className)}>
      <Text as='h3' className={styles.title}>
        {title}
      </Text>
      {description && <Text className={styles.description}>{description}</Text>}
      <MockFilterGroup chart={chart} />
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) {
          return child;
        }
        return React.cloneElement(child, { setChart });
      })}
    </div>
  );
};

ChartGroup.displayName = 'ChartGroup';
export default ChartGroup;
export { styles };
